import React, { useState } from 'react';
import { KeyRound, Mail, Phone, AlertCircle, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useMyAnagrafica } from '../../hooks/useMyAnagrafica';
import api from '../../services/api';

export default function DatiAccessoPage() {
  const { user } = useAuth();
  const { data: ana, loading } = useMyAnagrafica();

  // Stato cambio password
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwShow, setPwShow] = useState(false);
  const [pwSubmitting, setPwSubmitting] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSuccess, setPwSuccess] = useState(false);

  // Stato modifica email/telefono
  const [email, setEmail] = useState(user?.email ?? '');
  const [telefono, setTelefono] = useState('');
  const [contatSubmitting, setContatSubmitting] = useState(false);
  const [contatError, setContatError] = useState<string | null>(null);
  const [contatSuccess, setContatSuccess] = useState(false);

  React.useEffect(() => {
    if (user?.email && email === '') setEmail(user.email);
  }, [user, email]);

  const pwValid =
    pwCurrent.length > 0 &&
    pwNew.length >= 8 &&
    pwNew === pwConfirm &&
    pwNew !== pwCurrent;

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pwValid) return;
    setPwSubmitting(true);
    setPwError(null);
    setPwSuccess(false);
    try {
      await api.post('/api/auth/me/password', {
        current_password: pwCurrent,
        new_password: pwNew,
      });
      setPwSuccess(true);
      setPwCurrent('');
      setPwNew('');
      setPwConfirm('');
    } catch (err: any) {
      setPwError(err?.response?.data?.detail ?? 'Impossibile cambiare password.');
    } finally {
      setPwSubmitting(false);
    }
  };

  const submitContatti = async (e: React.FormEvent) => {
    e.preventDefault();
    setContatSubmitting(true);
    setContatError(null);
    setContatSuccess(false);
    try {
      const body: any = {};
      if (email && email !== user?.email) body.email = email;
      if (telefono.trim()) body.telefono = telefono.trim();
      await api.patch('/api/auth/me', body);
      setContatSuccess(true);
    } catch (err: any) {
      setContatError(err?.response?.data?.detail ?? 'Impossibile aggiornare i contatti.');
    } finally {
      setContatSubmitting(false);
    }
  };

  return (
    <div data-testid="dati-accesso-page" className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Dati di accesso</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Gestisci la tua password e i contatti che usiamo per le comunicazioni dell'account.
        </p>
      </div>

      {/* Account info read-only */}
      <div className="rounded-xl border bg-white p-6">
        <h2 className="text-sm font-semibold text-[#3a3a36] mb-4 uppercase tracking-wider">Il tuo account</h2>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-12 bg-[hsl(0,0%,94%)] rounded animate-pulse" />
            ))}
          </div>
        ) : (
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            <Field icon={<BadgeIcon />} label="Codice cliente" value={ana?.an_conto?.toString()} mono />
            <Field icon={<NameIcon />} label="Nome e cognome" value={[user?.nome, user?.cognome].filter(Boolean).join(' ')} />
            <Field icon={<RoleIcon />} label="Ruolo" value={(user?.ruolo === 'cliente' ? 'Cliente' : user?.ruolo) ?? '—'} />
            <Field icon={<UserAvatarIcon />} label="Email di accesso" value={user?.email} />
          </dl>
        )}
      </div>

      {/* Cambio password */}
      <form onSubmit={submitPassword} className="rounded-xl border bg-white p-6 space-y-4" data-testid="change-password-form">
        <div className="flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-[hsl(210,64%,29%)]" />
          <h2 className="text-lg font-semibold">Cambia password</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          La nuova password deve essere lunga almeno 8 caratteri e diversa dall'attuale.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <PasswordField
            label="Password attuale"
            value={pwCurrent}
            onChange={setPwCurrent}
            show={pwShow}
            onToggleShow={() => setPwShow(s => !s)}
            testId="pw-current"
            autoComplete="current-password"
          />
          <PasswordField
            label="Nuova password"
            value={pwNew}
            onChange={setPwNew}
            show={pwShow}
            onToggleShow={() => setPwShow(s => !s)}
            testId="pw-new"
            autoComplete="new-password"
            helper={pwNew && pwNew.length < 8 ? 'Minimo 8 caratteri' : null}
            error={pwNew && pwNew === pwCurrent ? 'Deve essere diversa dall\'attuale' : null}
          />
          <PasswordField
            label="Conferma password"
            value={pwConfirm}
            onChange={setPwConfirm}
            show={pwShow}
            onToggleShow={() => setPwShow(s => !s)}
            testId="pw-confirm"
            autoComplete="new-password"
            error={pwConfirm && pwNew !== pwConfirm ? 'Le password non coincidono' : null}
          />
        </div>

        {pwError && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800 flex items-center gap-2" data-testid="pw-error">
            <AlertCircle className="w-4 h-4" /> {pwError}
          </div>
        )}
        {pwSuccess && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 flex items-center gap-2" data-testid="pw-success">
            <CheckCircle2 className="w-4 h-4" /> Password aggiornata correttamente.
          </div>
        )}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!pwValid || pwSubmitting}
            data-testid="pw-submit"
            className="h-10 px-5 rounded-lg bg-[hsl(210,64%,29%)] text-white text-sm font-semibold hover:bg-[hsl(210,64%,32%)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {pwSubmitting ? 'Salvataggio…' : 'Aggiorna password'}
          </button>
        </div>
      </form>

      {/* Contatti */}
      <form onSubmit={submitContatti} className="rounded-xl border bg-white p-6 space-y-4" data-testid="update-contacts-form">
        <div className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-[hsl(210,64%,29%)]" />
          <h2 className="text-lg font-semibold">Contatti di servizio</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          L'email serve per il login e per gli avvisi. Per cambiare i dati di fatturazione scrivi a <a href="mailto:assistenza@dtksrl.it" className="underline">assistenza@dtksrl.it</a>.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Email di accesso</label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="email-input"
                className="w-full h-10 pl-9 pr-3 rounded-lg border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(210,64%,29%)]/30"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Telefono (opzionale)</label>
            <div className="relative">
              <Phone className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
              <input
                type="tel"
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                data-testid="phone-input"
                placeholder={ana?.telefono ?? 'es. 055 123 4567'}
                className="w-full h-10 pl-9 pr-3 rounded-lg border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(210,64%,29%)]/30"
              />
            </div>
          </div>
        </div>

        {contatError && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800 flex items-center gap-2" data-testid="contacts-error">
            <AlertCircle className="w-4 h-4" /> {contatError}
          </div>
        )}
        {contatSuccess && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 flex items-center gap-2" data-testid="contacts-success">
            <CheckCircle2 className="w-4 h-4" /> Contatti aggiornati.
          </div>
        )}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={contatSubmitting}
            data-testid="contacts-submit"
            className="h-10 px-5 rounded-lg bg-[hsl(210,64%,29%)] text-white text-sm font-semibold hover:bg-[hsl(210,64%,32%)] disabled:opacity-40 transition-colors"
          >
            {contatSubmitting ? 'Salvataggio…' : 'Salva contatti'}
          </button>
        </div>
      </form>
    </div>
  );
}

function PasswordField({
  label, value, onChange, show, onToggleShow, testId, autoComplete, helper, error,
}: {
  label: string;
  value: string;
  onChange: (s: string) => void;
  show: boolean;
  onToggleShow: () => void;
  testId: string;
  autoComplete?: string;
  helper?: string | null;
  error?: string | null;
}) {
  return (
    <div>
      <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          data-testid={testId}
          className={`w-full h-10 pl-3 pr-10 rounded-lg border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(210,64%,29%)]/30 ${error ? 'border-rose-300' : ''}`}
        />
        <button
          type="button"
          onClick={onToggleShow}
          className="absolute right-2 top-2 p-1 text-muted-foreground hover:text-foreground"
          tabIndex={-1}
          aria-label={show ? 'Nascondi' : 'Mostra'}
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      {error ? (
        <p className="mt-1 text-[11px] text-rose-700">{error}</p>
      ) : helper ? (
        <p className="mt-1 text-[11px] text-muted-foreground">{helper}</p>
      ) : null}
    </div>
  );
}

function Field({ icon, label, value, mono }: { icon: React.ReactNode; label: string; value?: string | null; mono?: boolean }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 w-8 h-8 rounded-lg bg-[hsl(210,40%,96%)] flex items-center justify-center text-[hsl(210,64%,29%)]">{icon}</div>
      <div>
        <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{label}</div>
        <div className={`text-sm ${mono ? 'font-mono' : ''}`}>{value || '—'}</div>
      </div>
    </div>
  );
}

const UserAvatarIcon = () => (<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" /><circle cx={9} cy={7} r={4} /></svg>);
const BadgeIcon = () => (<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M3 7l9-4 9 4-9 4-9-4z" /><path d="M21 10v6m-9 5v-7" /></svg>);
const NameIcon = () => (<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M16 21v-2a4 4 0 00-3-3.87M8 21v-2a4 4 0 013-3.87" /><circle cx={12} cy={7} r={4} /></svg>);
const RoleIcon = () => (<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M12 2l2.5 5L20 8l-4 4 1 6-5-3-5 3 1-6-4-4 5.5-1z" /></svg>);
